/**
 * Shipment Promotion Recovery — putting the shopper's selection back after a
 * shipment-only product leaves the cart.
 *
 * A shipment-only EA product (`ediblearrangement.availability_method` is just
 * "Shipment", see snippets/personalization-shipment-only.liquid) is not Goody,
 * but it moves the selection the same way: the personalization drawer's
 * `#commitShipmentPromotion` rewrites `ea-store-availability` to `shipment`, a
 * ship date, the store that ships it, and no store card. Its undo snapshot only
 * covers an add that fails; once the line is in the cart the snapshot is thrown
 * away, so a shopper who then emptied the cart was left on shipment for good.
 *
 * This module is the Goody recovery's twin for that case:
 *
 *   1. The drawer calls `stashPrePromotionSelection()` with the same snapshot it
 *      keeps for rollback, and `dropPrePromotionSelection()` when it rolls back.
 *   2. @theme/ea-cart-lock's "the cart holds anything" signal marks the stash as
 *      having seen the line land, and restores it when the cart is empty again.
 *
 * Why "cart empty" is the exit, not "no shipment lines": the promotion is
 * refused on a store-locked cart, so it only ever starts from an empty (or
 * Goody, already-shipment) cart, and the shipment line then locks the cart
 * until it is cleared. Every line in the cart was added under the promoted
 * selection, so the cart emptying is exactly the promotion ending.
 *
 * Why the seen-items mark: the promotion writes cart attributes BEFORE the add is
 * posted, and that write makes the cart-lock re-read a cart that is still empty.
 * Same window, and same fix, as the Goody recovery's `seenGoody`.
 *
 * Why a key of its own rather than `ea-pre-goody-selection`: that key's presence
 * is read by the drawer as "a store-less shipment record the Goody gate forced"
 * (`isGoodyForcedShipment`), and a promoted record names a shipping store.
 */

import { readStoreContext } from '@theme/ea-availability';
import { CART_ANY_ITEMS_EVENT, isCartNonEmpty } from '@theme/ea-cart-lock';
import { isRestorableSelection, applySelection } from '@theme/goody-fulfillment-recovery';
import { FULFILLMENT_TYPES } from '@theme/cart-contract';

/** localStorage key holding the parked pre-promotion selection. */
export const PRE_SHIPMENT_KEY = 'ea-pre-shipment-selection';

/**
 * @typedef {object} PrePromotionRecord
 * @property {object|null} selection the delivery/pickup selection to put back.
 * @property {boolean} seenItems whether the cart has held a line since the stash.
 * @property {number} savedAt epoch ms, for debugging a stuck record.
 */

/** @returns {PrePromotionRecord|null} */
function readRecord() {
  try {
    const raw = localStorage.getItem(PRE_SHIPMENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      selection: parsed.selection && typeof parsed.selection === 'object' ? parsed.selection : null,
      seenItems: parsed.seenItems === true,
      savedAt: Number(parsed.savedAt) || 0,
    };
  } catch (e) {
    return null;
  }
}

/** Writes the parked record. Silently no-ops when storage is unavailable. */
function writeRecord(record) {
  try {
    localStorage.setItem(PRE_SHIPMENT_KEY, JSON.stringify(record));
  } catch (e) {
    // Private mode / storage blocked — the shopper keeps today's behaviour.
  }
}

/**
 * Park the selection the drawer is about to promote to shipment.
 *
 * Only a restorable one (a zip, and delivery or pickup) is parked: unlike the
 * Goody stash, this record's presence carries no meaning of its own, so there
 * is no reason to write one with nothing in it.
 *
 * Never overwrites an existing record — the state that matters is the one
 * before the first promotion.
 *
 * @param {object|null} selection
 */
export function stashPrePromotionSelection(selection) {
  if (readRecord()) return;
  if (!isRestorableSelection(selection)) return;
  writeRecord({
    selection,
    // Normally false: the drawer stashes before /cart/add. Seeded rather than
    // assumed so a cart that already holds lines can still arm the restore.
    seenItems: isCartNonEmpty(),
    savedAt: Date.now(),
  });
}

/** Drop the parked record — the promotion was rolled back or has ended. */
export function dropPrePromotionSelection() {
  try {
    localStorage.removeItem(PRE_SHIPMENT_KEY);
  } catch (e) {
    // A record we can't remove is re-validated on next read.
  }
}

/**
 * Put the pre-promotion selection back, if it is still the shopper's to get back.
 *
 * Same rules as @theme/goody-fulfillment-recovery's restore: only when the
 * current record is still `shipment` on the same zip — anything else is a choice
 * the shopper made since, and is kept. When it acts, the parked record wins
 * field by field, because the promoted record has replaced the store, nulled the
 * card and rewritten the speed, date and label.
 *
 * @returns {'restored'|'kept'|'none'}
 */
export function restorePrePromotionSelection() {
  const record = readRecord();
  if (!record) return 'none';
  dropPrePromotionSelection();

  const current = readStoreContext();
  const parked = record.selection;
  const stillPromoted = String(current?.fulfillmentType || '') === FULFILLMENT_TYPES.shipment;
  const zipMoved = Boolean(current?.zipCode && parked?.zipCode && current.zipCode !== parked.zipCode);
  if (!stillPromoted || zipMoved || !isRestorableSelection(parked)) return 'kept';

  const restored = { ...current, ...parked };
  applySelection(restored, { syncCart: isCartNonEmpty() });
  return 'restored';
}

/**
 * React to the cart's non-empty signal: `true` arms the stash, a later `false`
 * completes the transition.
 * @param {boolean} active
 */
function onCartItemsSignal(active) {
  const record = readRecord();
  if (!record) return;

  if (active) {
    if (!record.seenItems) writeRecord({ ...record, seenItems: true });
    return;
  }

  // Never seen a line: the gap between the stash and the add, or an add that
  // failed without the drawer's rollback reaching us. Leave it for now.
  if (!record.seenItems) return;

  restorePrePromotionSelection();
}

document.addEventListener(CART_ANY_ITEMS_EVENT, (event) => {
  onCartItemsSignal(event.detail?.active === true);
});

// The cart-lock seeds its signals before this listener exists, so read the
// settled value directly — a page that LOADS with the cart already empty still
// completes the transition.
onCartItemsSignal(isCartNonEmpty());
